import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
    Box,
    Typography,
    makeStyles,
    MenuItem,
    TextField
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { CustomSwitch2 } from 'web/src/app/root/components/common/switch'
import { Button } from 'web/src/app/root/components/common'
import { getBaitActivityThresholdDataFromString } from 'web/src/utils'
import { DeviceConfirmThresholdResetModal } from 'web/src/app/devices/components/device-confirm-threshold-reset-modal'
import {
    DEFAULT_BAIT_CALIBRATION_WEIGHT_GRAMS,
    DEVICE_TYPE
} from '../../constants'
import { LocationConfirmThresholdResetModal } from 'web/src/app/locations/components/location-settings/location-confirm-threshold-reset-modal'

const useStyles = makeStyles(theme => ({
    container: {
        maxWidth: 1200,
        padding: theme.spacing(3),
        overflowX: 'auto'
    },
    header: {
        fontSize: 32,
        fontWeight: 600,
        marginBottom: theme.spacing(4),
        color: theme.textPrimary
    },
    serialNumber: {
        color: theme.textSecondary,
        flex: 1,
        fontStyle: 'italic'
    },
    pageTitle: {
        alignItems: 'baseline'
    },
    settingRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(3, 0),
        borderBottom: `1px solid ${theme.grayLight}`,
        '&:last-child': {
            borderBottom: 'none'
        }
    },
    settingLeft: {
        flex: 1,
        paddingRight: theme.spacing(4)
    },
    settingRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap'
    },
    settingTitle: {
        fontSize: 16,
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
        color: theme.textPrimary
    },
    settingDescription: {
        fontSize: 14,
        color: theme.textSecondary
    },
    select: {
        minWidth: 120,
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.borderRadius
        }
    },
    input: {
        width: 90,
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.borderRadius
        }
    },
    baitTypeInput: {
        width: 200
    },
    baitAmountInput: {
        width: 100
    },
    saveButton: {
        minWidth: 100
    },
    thresholdChip: {
        marginTop: theme.spacing(1)
    },
    btnsWrapper: {
        marginTop: 20,
        display: 'flex',
        justifyContent: 'flex-end'
    }
}))

export const BaitSettings = ({
    data,
    reloadDevice,
    canUpdate,
    updateData,
    isDevice,
    isLocationSettings,
    fullSave,
    overridingLocationSettings,
    fullCancel
}) => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const locale = useSelector(state => state.main.locale)
    const theme = useSelector(state => state.main.theme)

    const [
        isConfirmThresholdResetModalOpen,
        setIsConfirmThresholdResetModalOpen
    ] = useState(false)

    const hasOverridingLocationBaitSettings = useMemo(() => {
        return overridingLocationSettings?.data?.hasOwnProperty('bait_settings')
    }, [overridingLocationSettings?.data?.bait_settings])

    const usesLocationBaitSettings =
        hasOverridingLocationBaitSettings && !data.override_location_settings
    const baitSettings = usesLocationBaitSettings
        ? overridingLocationSettings.data.bait_settings
        : data

    // State for bait monitoring
    const [baitMonitoring, setBaitMonitoring] = useState(
        baitSettings.is_bait_monitoring_enabled
    )

    const activityThresholdData = useMemo(() => {
        if (!baitSettings.activity_alert_threshold) return null
        return getBaitActivityThresholdDataFromString(
            baitSettings.activity_alert_threshold
        )
    }, [baitSettings.activity_alert_threshold])

    const [activityThreshold, setActivityThreshold] = useState(10)
    const [activityThresholdUnitAmount, setActivityThresholdUnitAmount] =
        useState(1)
    const [activityThresholdUnit, setActivityThresholdUnit] = useState('d')

    // State for bait type
    const [baitManufacturerAndType, setBaitManufacturerAndType] = useState(
        baitSettings.bait_manufacturer_and_type ?? ''
    )
    const [baitWeightGrams, setBaitWeightGrams] = useState(
        baitSettings.bait_weight_grams ?? ''
    )

    // State for calibration
    const [baitCalibrationWeightGrams, setBaitCalibrationWeightGrams] =
        useState(
            baitSettings.bait_calibration_weight_grams ??
                DEFAULT_BAIT_CALIBRATION_WEIGHT_GRAMS
        )

    const onBaitMonitoringChange = event => {
        setBaitMonitoring(event.target.checked)
        // handleSaveBaitMonitoring(event.target.checked)
        updateData?.({
            is_bait_monitoring_enabled: event.target.checked
        })
    }

    // const handleSaveBaitMonitoring = value => {
    //     dispatch(
    //         actions.updateDeviceRequest(data.id, {
    //             is_bait_monitoring_enabled: value
    //         })
    //     )
    // }

    const onSaveActivityThreshold = () => {
        setIsConfirmThresholdResetModalOpen(true)
    }

    const onConfirmThresholdReset = () => {
        console.log('Reset activity threshold')
        handleSaveActivityThreshold()
        setIsConfirmThresholdResetModalOpen(false)
    }

    const handleSaveActivityThreshold = () => {
        let newActivityThreshold = activityThreshold
        if (
            !newActivityThreshold ||
            typeof newActivityThreshold !== 'number' ||
            newActivityThreshold < 1
        ) {
            newActivityThreshold = 1
        }
        let newActivityThresholdUnitAmount = activityThresholdUnitAmount
        if (
            !newActivityThresholdUnitAmount ||
            typeof newActivityThresholdUnitAmount !== 'number' ||
            newActivityThresholdUnitAmount < 1
        ) {
            newActivityThresholdUnitAmount = 1
        }
        const newActivityThresholdString = `${newActivityThreshold}_${newActivityThresholdUnitAmount}_${activityThresholdUnit}`
        updateData?.({
            activity_alert_threshold: newActivityThresholdString
        })
        // dispatch(
        //     actions.updateDeviceRequest(data.id, {
        //         activity_alert_threshold: newActivityThresholdString
        //     })
        // )
    }

    const handleSaveBaitManufacturerAndTypeAndWeightGrams = () => {
        let newBaitWeightGrams = baitWeightGrams
        if (
            !newBaitWeightGrams ||
            typeof newBaitWeightGrams !== 'number' ||
            newBaitWeightGrams < 1
        ) {
            newBaitWeightGrams = 0
        }
        updateData?.({
            bait_manufacturer_and_type: baitManufacturerAndType,
            bait_weight_grams: newBaitWeightGrams
        })
        // dispatch(
        //     actions.updateDeviceRequest(data.id, {
        //         bait_manufacturer_and_type: baitManufacturerAndType,
        //         bait_weight_grams: newBaitWeightGrams
        //     })
        // )
    }

    const handleSaveBaitCalibrationWeightGrams = () => {
        updateData?.({
            bait_calibration_weight_grams: baitCalibrationWeightGrams
        })
        // dispatch(
        //     actions.updateDeviceRequest(data.id, {
        //         bait_calibration_weight_grams: baitCalibrationWeightGrams
        //     })
        // )
    }

    const onFullSave = useCallback(() => {
        if (fullSave) {
            const threshold = parseInt(activityThreshold, 10) || 1
            const unitAmount = parseInt(activityThresholdUnitAmount, 10) || 1
            const newActivityThresholdString = `${threshold}_${unitAmount}_${activityThresholdUnit}`
            const weightGrams = parseInt(baitWeightGrams, 10) || 0
            const newData = {
                is_bait_monitoring_enabled: baitMonitoring,
                activity_alert_threshold: newActivityThresholdString,
                bait_manufacturer_and_type: baitManufacturerAndType,
                bait_weight_grams: weightGrams,
                bait_calibration_weight_grams: baitCalibrationWeightGrams
            }
            fullSave(newData)
        }
    }, [
        fullSave,
        activityThreshold,
        activityThresholdUnitAmount,
        activityThresholdUnit,
        baitManufacturerAndType,
        baitWeightGrams,
        baitCalibrationWeightGrams,
        baitMonitoring
    ])

    useEffect(() => {
        setBaitMonitoring(baitSettings.is_bait_monitoring_enabled)
        setBaitManufacturerAndType(
            baitSettings.bait_manufacturer_and_type ?? ''
        )
        setBaitWeightGrams(baitSettings.bait_weight_grams ?? '')
        setBaitCalibrationWeightGrams(
            baitSettings.bait_calibration_weight_grams ??
                DEFAULT_BAIT_CALIBRATION_WEIGHT_GRAMS
        )
    }, [baitSettings])

    useEffect(() => {
        if (activityThresholdData) {
            setActivityThreshold(activityThresholdData.threshold)
            setActivityThresholdUnitAmount(activityThresholdData.unitsAmount)
            setActivityThresholdUnit(activityThresholdData.unit)
        }
    }, [activityThresholdData])

    return (
        <>
            {usesLocationBaitSettings ? (
                <div
                    style={{
                        marginBottom: 10,
                        padding: 20,
                        border: theme.border,
                        borderRadius: theme.borderRadius
                    }}>
                    {`ⓘ ${locale.locations.lBasedSettingsAreAppliedYouCanNotEdit}`}
                </div>
            ) : null}
            {isDevice &&
            data.device_type === DEVICE_TYPE['jerry-bait-rat'] &&
            hasOverridingLocationBaitSettings ? (
                <Box className={classes.settingRow}>
                    <Box className={classes.settingLeft}>
                        <Typography className={classes.settingTitle}>
                            {locale.devices.overrideLocationSettings}
                        </Typography>
                        <Typography className={classes.settingDescription}>
                            {locale.devices.overrideLocationSettingsDescription}
                        </Typography>
                    </Box>
                    <Box className={classes.settingRight}>
                        <CustomSwitch2
                            checked={data.override_location_settings}
                            disabled={!canUpdate}
                            onChange={event =>
                                updateData?.({
                                    override_location_settings:
                                        event.target.checked
                                })
                            }
                        />
                    </Box>
                </Box>
            ) : null}
            {/* Bait Monitoring */}
            <Box className={classes.settingRow}>
                <Box className={classes.settingLeft}>
                    <Typography className={classes.settingTitle}>
                        {locale.devices.baitMonitoring}
                    </Typography>
                    <Typography className={classes.settingDescription}>
                        {locale.devices.enablesOrDisablesBaitWeightTracking}
                    </Typography>
                </Box>
                <Box className={classes.settingRight}>
                    <CustomSwitch2
                        disabled={!canUpdate || usesLocationBaitSettings}
                        checked={baitMonitoring}
                        onChange={onBaitMonitoringChange}
                    />
                </Box>
            </Box>

            {/* Calibration */}
            <Box className={classes.settingRow}>
                <Box className={classes.settingLeft}>
                    <Typography className={classes.settingTitle}>
                        {locale.devices.calibration}
                    </Typography>
                    <Typography className={classes.settingDescription}>
                        {locale.devices.notifiedWhenBaitWeightLowerThan}
                    </Typography>
                </Box>
                <Box className={classes.settingRight}>
                    <Typography style={{ marginRight: 8 }}>
                        {locale.devices.baitPad}:
                    </Typography>
                    <TextField
                        select
                        value={baitCalibrationWeightGrams}
                        onChange={e =>
                            setBaitCalibrationWeightGrams(
                                Number(e.target.value)
                            )
                        }
                        variant="outlined"
                        size="small"
                        className={classes.select}
                        disabled={!canUpdate || usesLocationBaitSettings}>
                        {[10, 20, 30, 40, 50, 60, 70].map(num => (
                            <MenuItem key={num} value={num}>
                                {num} g
                            </MenuItem>
                        ))}
                    </TextField>
                    {updateData && !usesLocationBaitSettings ? (
                        <Button
                            className={classes.saveButton}
                            onClick={handleSaveBaitCalibrationWeightGrams}
                            disabled={!canUpdate}>
                            {locale.common.save}
                        </Button>
                    ) : null}
                </Box>
            </Box>

            {/* Bait Type */}
            <Box className={classes.settingRow}>
                <Box className={classes.settingLeft}>
                    <Typography className={classes.settingTitle}>
                        {locale.devices.baitType}
                    </Typography>
                    <Typography className={classes.settingDescription}>
                        {locale.devices.whichBaitAreYouUsing}
                    </Typography>
                </Box>
                <Box className={classes.settingRight}>
                    <TextField
                        value={baitManufacturerAndType}
                        onChange={e =>
                            setBaitManufacturerAndType(e.target.value)
                        }
                        placeholder={locale.devices.manufacturerAndType}
                        variant="outlined"
                        size="small"
                        className={[classes.baitTypeInput, classes.input]}
                        disabled={!canUpdate || usesLocationBaitSettings}
                    />
                    <TextField
                        value={baitWeightGrams}
                        onChange={e =>
                            setBaitWeightGrams(Number(e.target.value))
                        }
                        type="number"
                        variant="outlined"
                        size="small"
                        className={[classes.baitAmountInput, classes.input]}
                        InputProps={{
                            endAdornment: <Typography>g</Typography>,
                            inputProps: {
                                min: 1
                            }
                        }}
                        disabled={!canUpdate || usesLocationBaitSettings}
                    />
                    {updateData && !usesLocationBaitSettings ? (
                        <Button
                            className={classes.saveButton}
                            onClick={
                                handleSaveBaitManufacturerAndTypeAndWeightGrams
                            }
                            disabled={!canUpdate}>
                            {locale.common.save}
                        </Button>
                    ) : null}
                </Box>
            </Box>

            {/* Activity Alert Threshold */}
            <Box className={classes.settingRow}>
                <Box className={classes.settingLeft}>
                    <Typography className={classes.settingTitle}>
                        {locale.devices.activityAlertThreshold}
                    </Typography>
                    <Typography className={classes.settingDescription}>
                        {
                            locale.devices
                                .whenTheNotificationAlertShouldBeTriggered
                        }
                    </Typography>
                </Box>
                <Box className={classes.settingRight}>
                    <TextField
                        type="number"
                        value={activityThreshold}
                        onChange={e =>
                            setActivityThreshold(Number(e.target.value))
                        }
                        InputProps={{
                            inputProps: {
                                min: 1
                            }
                        }}
                        variant="outlined"
                        size="small"
                        className={classes.input}
                        disabled={!canUpdate || usesLocationBaitSettings}
                    />
                    <Typography>{locale.devices.per}</Typography>
                    <TextField
                        value={activityThresholdUnitAmount}
                        type="number"
                        onChange={e =>
                            setActivityThresholdUnitAmount(
                                Number(e.target.value)
                            )
                        }
                        InputProps={{
                            inputProps: {
                                min: 1
                            }
                        }}
                        variant="outlined"
                        size="small"
                        className={classes.input}
                        disabled={!canUpdate || usesLocationBaitSettings}
                    />
                    <TextField
                        select
                        value={activityThresholdUnit}
                        onChange={e => setActivityThresholdUnit(e.target.value)}
                        variant="outlined"
                        size="small"
                        className={classes.select}
                        disabled={!canUpdate || usesLocationBaitSettings}>
                        <MenuItem value="h">{locale.devices.hours}</MenuItem>
                        <MenuItem value="d">{locale.devices.days}</MenuItem>
                    </TextField>
                    {updateData && !usesLocationBaitSettings ? (
                        <Button
                            className={classes.saveButton}
                            onClick={onSaveActivityThreshold}
                            disabled={!canUpdate || usesLocationBaitSettings}>
                            {locale.common.save}
                        </Button>
                    ) : null}
                </Box>
            </Box>

            {isDevice && reloadDevice ? (
                <DeviceConfirmThresholdResetModal
                    open={isConfirmThresholdResetModalOpen}
                    setOpen={setIsConfirmThresholdResetModalOpen}
                    onConfirmClick={onConfirmThresholdReset}
                    deviceId={data.id}
                />
            ) : null}
            {isLocationSettings && updateData ? (
                <LocationConfirmThresholdResetModal
                    open={isConfirmThresholdResetModalOpen}
                    onClose={() => setIsConfirmThresholdResetModalOpen(false)}
                    onConfirmClick={onConfirmThresholdReset}
                />
            ) : null}
            {fullSave && fullCancel ? (
                <div className={classes.btnsWrapper}>
                    <Button style={{ marginRight: 8 }} onClick={onFullSave}>
                        {locale.common.save}
                    </Button>
                    <Button reverted onClick={fullCancel}>
                        {locale.common.cancel}
                    </Button>
                </div>
            ) : null}
        </>
    )
}
